import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DocumentModal } from "@/pages/documents/DocumentModal";
import {
  HubSupportTicketMessageThread,
} from "@/components/HubSupportTicketMessageThread";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { apiFetch } from "@/lib/api";
import { inferCarrierFromTrackingNumber } from "@/lib/inferCarrierFromTracking";
import { mrpLocationLabelForCode } from "@/lib/mrp";
import {
  formatPurchaseRequestQuantity,
  purchaseRequestIsInventoryReview,
  purchaseRequestItemLabel,
  purchaseRequestKindLabel,
  purchaseRequestMessagesAsHubThread,
  PURCHASE_REVIEW_OUTCOME_OPTIONS,
  type PurchaseRequestMessage,
  type PurchaseRequestRow,
  type PurchaseReviewOutcome,
} from "@/lib/purchaseRequest";

function PurchaseRequestStatusBadge({ status }: { status: PurchaseRequestRow["status"] }): JSX.Element {
  if (status === "ordered") {
    return <Badge tone="success">Ordered</Badge>;
  }
  if (status === "reviewed") {
    return <Badge tone="success">Reviewed</Badge>;
  }
  if (status === "refused") {
    return <Badge tone="danger">Refused</Badge>;
  }
  if (status === "cancelled") {
    return <Badge tone="neutral">Cancelled</Badge>;
  }
  return <Badge tone="warning">Open</Badge>;
}

export function PurchaseRequestManageModal({
  requestId,
  onClose,
}: {
  requestId: string;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [messageDraft, setMessageDraft] = useState("");
  const [messageError, setMessageError] = useState<string | null>(null);
  const [expectedArrivalDate, setExpectedArrivalDate] = useState("");
  const [purchasedFrom, setPurchasedFrom] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingCarrier, setTrackingCarrier] = useState("");
  const [refusalReason, setRefusalReason] = useState("");
  const [reviewOutcome, setReviewOutcome] = useState<PurchaseReviewOutcome>("no_changes_needed");
  const [reviewOutcomeNote, setReviewOutcomeNote] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ["mrp", "purchase-requests", "detail", requestId],
    queryFn: () =>
      apiFetch<{ request: PurchaseRequestRow; messages: PurchaseRequestMessage[] }>(
        `/api/mrp/purchase-requests/${requestId}`,
      ),
  });

  const request = detailQuery.data?.request;
  const messages = detailQuery.data?.messages ?? [];
  const isInventoryReview = request != null && purchaseRequestIsInventoryReview(request);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-requests"] });
    void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
    void queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-requests", "detail", requestId] });
  };

  const messageMutation = useMutation({
    mutationFn: (body: string) =>
      apiFetch<{ ok: boolean; messages: PurchaseRequestMessage[] }>(
        `/api/mrp/purchase-requests/${requestId}/messages`,
        { method: "POST", body: { body } },
      ),
    onSuccess: (result) => {
      setMessageDraft("");
      setMessageError(null);
      queryClient.setQueryData(
        ["mrp", "purchase-requests", "detail", requestId],
        (previous: { request: PurchaseRequestRow; messages: PurchaseRequestMessage[] } | undefined) =>
          previous ? { ...previous, messages: result.messages } : previous,
      );
      invalidate();
    },
    onError: (error: Error) => {
      setMessageError(error.message);
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ request: PurchaseRequestRow }>(`/api/mrp/purchase-requests/${requestId}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      setActionError(null);
      invalidate();
      onClose();
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  const canMessage = request?.status === "open";
  const requester =
    request?.requester_display_name != null && request.requester_display_name.trim() !== ""
      ? request.requester_display_name
      : request?.requester_email ?? "";

  const modalTitle = request
    ? isInventoryReview
      ? `Inventory review #${request.id}`
      : `Purchase request #${request.id}`
    : "Purchase request";

  return (
    <DocumentModal
      title={modalTitle}
      showCloseButton={false}
      onClose={onClose}
      shellClassName="max-w-2xl"
    >
      {detailQuery.isLoading && !request ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : detailQuery.isError ? (
        <p className="text-sm text-red-600">{(detailQuery.error as Error).message}</p>
      ) : request == null ? (
        <p className="text-sm text-slate-600">Request not found.</p>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={isInventoryReview ? "neutral" : "neutral"}>
              {purchaseRequestKindLabel(request.kind)}
            </Badge>
            <PurchaseRequestStatusBadge status={request.status} />
            <span className="text-xs text-slate-500">Submitted {request.created_at}</span>
          </div>

          {isInventoryReview ? (
            <p className="rounded-md border border-amber-100 bg-amber-50/80 px-3 py-2 text-sm text-amber-950">
              Inventory changed after an approved discrepancy. Review open purchase orders and purchase requests for
              this SKU — you may need to cancel, reduce, or add purchases. You are not placing a new order here.
            </p>
          ) : null}

          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Item</dt>
              <dd className="mt-1 text-slate-900">{purchaseRequestItemLabel(request)}</dd>
              {request.sku != null && request.item_description.trim() !== "" ? (
                <dd className="mt-1 text-xs text-slate-500">{request.item_description}</dd>
              ) : null}
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {isInventoryReview ? "Inventory change" : "Quantity"}
              </dt>
              <dd className="mt-1 tabular-nums text-slate-900">{formatPurchaseRequestQuantity(request)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Receiving site</dt>
              <dd className="mt-1 text-slate-900">{mrpLocationLabelForCode(request.location_code as "sgs")}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Requester</dt>
              <dd className="mt-1 text-slate-900">{requester}</dd>
            </div>
            {request.page_path != null && request.page_path.trim() !== "" ? (
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Source</dt>
                <dd className="mt-1">
                  <Link to={request.page_path} className="text-sm text-brand-700 hover:underline">
                    View inventory discrepancy
                  </Link>
                </dd>
              </div>
            ) : null}
            {request.notes != null && request.notes.trim() !== "" ? (
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {isInventoryReview ? "Context" : "Requester notes"}
                </dt>
                <dd className="mt-1 whitespace-pre-wrap text-slate-900">{request.notes}</dd>
              </div>
            ) : null}
          </dl>

          <HubSupportTicketMessageThread
            messages={purchaseRequestMessagesAsHubThread(messages)}
            draft={messageDraft}
            onDraftChange={setMessageDraft}
            onSubmit={() => {
              void messageMutation.mutateAsync(messageDraft.trim());
            }}
            submitting={messageMutation.isPending}
            canReply={canMessage}
            error={messageError}
            composeLabel={isInventoryReview ? "Message to approver" : "Message to requester"}
            viewer="admin"
            staffPartyLabel="Purchasing"
          />

          {request.status === "open" && isInventoryReview ? (
            <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50/60 p-4">
              <p className="text-sm font-medium text-slate-900">Record review outcome</p>
              <fieldset className="space-y-2">
                <legend className="text-xs font-medium uppercase tracking-wide text-slate-500">Outcome</legend>
                {PURCHASE_REVIEW_OUTCOME_OPTIONS.map((option) => (
                  <label key={option.value} className="flex cursor-pointer items-start gap-2 text-sm text-slate-800">
                    <input
                      type="radio"
                      name="purchase-review-outcome"
                      value={option.value}
                      checked={reviewOutcome === option.value}
                      onChange={() => setReviewOutcome(option.value)}
                      className="mt-1"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </fieldset>
              <div>
                <Label htmlFor="purchase-review-outcome-note">
                  Notes{reviewOutcome === "other" ? " (required)" : " (optional)"}
                </Label>
                <textarea
                  id="purchase-review-outcome-note"
                  rows={3}
                  value={reviewOutcomeNote}
                  onChange={(event) => setReviewOutcomeNote(event.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="What did you check or change on open purchases?"
                />
              </div>
              {actionError != null ? <p className="text-sm text-red-600">{actionError}</p> : null}
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  disabled={
                    resolveMutation.isPending
                    || (reviewOutcome === "other" && reviewOutcomeNote.trim() === "")
                  }
                  onClick={() => {
                    void resolveMutation.mutateAsync({
                      status: "reviewed",
                      review_outcome: reviewOutcome,
                      review_outcome_note: reviewOutcomeNote.trim() === "" ? null : reviewOutcomeNote.trim(),
                    });
                  }}
                >
                  {resolveMutation.isPending ? "Saving…" : "Mark reviewed"}
                </Button>
              </div>
            </div>
          ) : null}

          {request.status === "open" && !isInventoryReview ? (
            <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50/60 p-4">
              <p className="text-sm font-medium text-slate-900">Resolve request</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="purchase-request-expected-arrival">Expected arrival (optional)</Label>
                  <Input
                    id="purchase-request-expected-arrival"
                    type="date"
                    value={expectedArrivalDate}
                    onChange={(event) => setExpectedArrivalDate(event.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="purchase-request-purchased-from">Purchased from (optional)</Label>
                  <Input
                    id="purchase-request-purchased-from"
                    value={purchasedFrom}
                    onChange={(event) => setPurchasedFrom(event.target.value)}
                    className="mt-1"
                    placeholder="Supplier or vendor"
                  />
                </div>
                <div>
                  <Label htmlFor="purchase-request-tracking-number">Tracking number (optional)</Label>
                  <Input
                    id="purchase-request-tracking-number"
                    value={trackingNumber}
                    onChange={(event) => setTrackingNumber(event.target.value)}
                    onBlur={() => {
                      if (trackingCarrier.trim() !== "") {
                        return;
                      }
                      const inferred = inferCarrierFromTrackingNumber(trackingNumber);
                      if (inferred != null) {
                        setTrackingCarrier(inferred);
                      }
                    }}
                    className="mt-1 font-mono"
                    placeholder="Carrier tracking number"
                  />
                </div>
                <div>
                  <Label htmlFor="purchase-request-tracking-carrier">Carrier (optional)</Label>
                  <Input
                    id="purchase-request-tracking-carrier"
                    value={trackingCarrier}
                    onChange={(event) => setTrackingCarrier(event.target.value)}
                    className="mt-1"
                    placeholder="Auto-filled from tracking when recognized"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="purchase-request-refusal-reason">Refusal reason (required to refuse)</Label>
                <textarea
                  id="purchase-request-refusal-reason"
                  rows={2}
                  value={refusalReason}
                  onChange={(event) => setRefusalReason(event.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="Explain why this request cannot be ordered…"
                />
              </div>
              {actionError != null ? <p className="text-sm text-red-600">{actionError}</p> : null}
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={resolveMutation.isPending || refusalReason.trim() === ""}
                  onClick={() => {
                    void resolveMutation.mutateAsync({
                      status: "refused",
                      refusal_reason: refusalReason.trim(),
                    });
                  }}
                >
                  Refuse request
                </Button>
                <Button
                  type="button"
                  disabled={resolveMutation.isPending}
                  onClick={() => {
                    void resolveMutation.mutateAsync({
                      status: "ordered",
                      expected_arrival_date: expectedArrivalDate.trim() === "" ? null : expectedArrivalDate,
                      purchased_from: purchasedFrom.trim() === "" ? null : purchasedFrom.trim(),
                      tracking_number: trackingNumber.trim() === "" ? null : trackingNumber.trim(),
                      tracking_carrier: trackingCarrier.trim() === "" ? null : trackingCarrier.trim(),
                    });
                  }}
                >
                  {resolveMutation.isPending ? "Saving…" : "Mark ordered"}
                </Button>
              </div>
            </div>
          ) : null}

          <div className="flex justify-end border-t border-slate-100 pt-4">
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </DocumentModal>
  );
}
